import { ReportJobStatus } from "constants/index";
import { ReportJob } from "helpers/types";
import { sanitizeReportErrorMessage } from "./extractReportErrorMessage";

export const REPORT_JOB_POLL_INTERVAL_MS = 1_000;
// Matches the backend IN_PROGRESS staleness guard (20 min): if the worker
// died silently, stop polling and surface a timeout instead of spinning.
export const REPORT_JOB_TIMEOUT_MS = 20 * 60_000;
export const REPORT_JOB_EXPIRED_MESSAGE =
  "The report is no longer available. Please recalculate.";
export const REPORT_JOB_TIMEOUT_MESSAGE =
  "Report generation timed out. Please try again.";

const TERMINAL_STATUSES = new Set([
  ReportJobStatus.COMPLETED,
  ReportJobStatus.FAILED,
  ReportJobStatus.NOT_FOUND,
]);

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

export async function pollReportJob<T>(
  initial: ReportJob<T>,
  fetchStatus: (key: string) => Promise<ReportJob<T>>,
): Promise<ReportJob<T>> {
  let job = initial;
  const deadline = Date.now() + REPORT_JOB_TIMEOUT_MS;
  while (!TERMINAL_STATUSES.has(job.status)) {
    if (Date.now() >= deadline) {
      return {
        ...job,
        status: ReportJobStatus.FAILED,
        status_message: REPORT_JOB_TIMEOUT_MESSAGE,
      };
    }
    await sleep(REPORT_JOB_POLL_INTERVAL_MS);
    job = await fetchStatus(initial.key);
  }
  return job;
}

export function reportJobErrorMessage<T>(
  job: ReportJob<T>,
  fallback: string,
): string {
  if (job.status === ReportJobStatus.NOT_FOUND) {
    return REPORT_JOB_EXPIRED_MESSAGE;
  }
  return sanitizeReportErrorMessage(job.status_message ?? "", fallback);
}
